import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

import { TenantService } from "@/services/tenantService";
import { getTenantScope, setTenantScope, subscribeTenantScope } from "@/lib/tenantScope";

/**
 * The tenant a SuperAdmin is working inside, for pages that manage
 * tenant-owned data (users, roles).
 *
 * A SuperAdmin owns no tenant, so these pages need one named explicitly.
 * Without it the API answers from the platform database only: shared
 * tenants merged together, dedicated tenants invisible. The selection is
 * ambient — `@/lib/api` sends it on every request — so a page passes
 * `scopeParams` to its list calls and nothing else.
 *
 * For a tenant user the hook is inert: `isSuperAdmin` is false, `scopeParams`
 * is empty, and no tenant list is fetched. Their JWT already scopes them.
 *
 * @returns {{
 *   isSuperAdmin: boolean,
 *   tenants: Array<{id: string, slug: string, name: string, dbMode: string, isActive: boolean, isDeleted: boolean, adLoginEnabled: boolean}>,
 *   tenantId: string,
 *   tenant: object|null,
 *   setTenantId: (id: string) => void,
 *   isLoading: boolean,
 *   isReady: boolean,
 *   scopeParams: object,
 * }}
 */
export const useTenantScope = ({ includeClosed = false } = {}) => {
  const isSuperAdmin = useSelector((state) => !!state.auth?.user?.isSuperAdmin);

  const [tenants, setTenants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tenantId, setTenantIdState] = useState(() => (isSuperAdmin ? getTenantScope() : ""));

  // Keep every mounted consumer in step — two pages can be alive at once
  // (a page and a modal), and the scope is one value for the session.
  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    return subscribeTenantScope(setTenantIdState);
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) {
      setTenants([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        // Active tenants only, unless the page says otherwise — a
        // suspended tenant is not something to hand out roles in, and its
        // API calls are rejected. `includeClosed` is Record Lookup: a
        // subject-access request, an erasure request or a complaint arrives
        // after an organisation is suspended or closed, and the records are
        // still there to answer for.
        const res = await TenantService.fetchTenantList(includeClosed ? { pagination: false, includeDeleted: true } : { pagination: false, isActive: true });
        if (cancelled) return;

        const rows = (res?.data?.tenants ?? []).map((t) => ({
          id: t.id,
          slug: t.slug,
          name: t.name || t.slug,
          dbMode: t.dbMode,
          // The state travels with the row so the picker can label it. A
          // list that includes closed tenants and does not say which they
          // are is worse than one that excludes them.
          isActive: t.isActive !== false,
          isDeleted: !!t.isDeleted,
          // Whether this tenant signs in through Active Directory. Travels
          // with the row because the pages that branch on it are about the
          // SCOPED tenant, not the signed-in user — a SuperAdmin owns no
          // tenant, so their session says nothing about any of these.
          adLoginEnabled: !!t.adLoginEnabled,
        }));
        // By the name, because the name is what the picker shows.
        rows.sort((a, b) => a.name.localeCompare(b.name));
        setTenants(rows);

        // Keep a stored selection only while it still resolves — a
        // tenant can be deleted or suspended between visits.
        const stored = getTenantScope();
        const valid = rows.some((t) => t.id === stored);
        // A closed or suspended tenant is a place to look, never the one to
        // default someone into.
        if (!valid) setTenantScope(rows.find((t) => t.isActive && !t.isDeleted)?.id ?? rows[0]?.id ?? "");
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        toast.error("Failed to load the tenant list");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, includeClosed]);

  const setTenantId = useCallback((next) => setTenantScope(next), []);

  const tenant = useMemo(() => tenants.find((t) => t.id === tenantId) ?? null, [tenants, tenantId]);

  // Sent explicitly on list calls as well as via the ambient header. The
  // server prefers the explicit value, and the services that page their
  // results need it to scope counts and filters, not just rows.
  const scopeParams = useMemo(() => (isSuperAdmin && tenantId ? { tenantId } : {}), [isSuperAdmin, tenantId]);

  return {
    isSuperAdmin,
    tenants,
    tenantId,
    tenant,
    setTenantId,
    isLoading,
    // False while a SuperAdmin has no resolved tenant yet. Pages hold
    // their table until then, so the first request is never an
    // unscoped cross-tenant read.
    isReady: !isSuperAdmin || !!tenantId,
    scopeParams,
  };
};

/**
 * The current scope only — no tenant list, no request. For components that
 * need to scope their own calls but do not render the picker: the add/edit
 * modals, which would otherwise re-fetch the whole tenant list every time
 * they open.
 *
 * @returns {{ isSuperAdmin: boolean, tenantId: string, scopeParams: object }}
 */
export const useTenantScopeParams = () => {
  const isSuperAdmin = useSelector((state) => !!state.auth?.user?.isSuperAdmin);
  const [tenantId, setTenantId] = useState(() => (isSuperAdmin ? getTenantScope() : ""));

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    return subscribeTenantScope(setTenantId);
  }, [isSuperAdmin]);

  const scopeParams = useMemo(() => (isSuperAdmin && tenantId ? { tenantId } : {}), [isSuperAdmin, tenantId]);

  return { isSuperAdmin, tenantId, scopeParams };
};

export default useTenantScope;
